(() => {
  if (customElements.get('creator-video-brief')) return;

  class CreatorVideoBrief extends HTMLElement {
    constructor() {
      super();
      this.activeKey = null;
      this.animationTimer = null;
      this.carouselFrame = null;
      this.initialRouteLockUntil = 0;
      this.previewParameterNames = new Set([
        'oseid',
        'preview_theme_id',
        'pb',
        '_fd',
        'surface_detail',
        'surface_inter_position'
      ]);
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFinderChange = this.handleFinderChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.updateCarouselControls = this.updateCarouselControls.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.tabs = Array.from(this.querySelectorAll('[data-brief-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-brief-panel]'));
      this.finder = this.querySelector('[data-brief-finder]');
      this.status = this.querySelector('[data-brief-status]');
      this.track = this.querySelector('[data-brief-track]');
      this.rail = this.querySelector('.creator-video-brief__rail');
      this.previousButton = this.querySelector('[data-brief-scroll="previous"]');
      this.nextButton = this.querySelector('[data-brief-scroll="next"]');
      this.paramName = this.dataset.paramName || 'tutorial';
      this.updateUrlOnChange = this.dataset.updateUrl === 'true';
      this.sectionId = this.dataset.sectionId || '';
      this.tabByKey = new Map();
      this.panelByKey = new Map();

      this.tabs.forEach((tab) => {
        const key = tab.dataset.briefKey;
        if (key && !this.tabByKey.has(key)) {
          this.tabByKey.set(key, tab);
        }
      });

      this.panels.forEach((panel) => {
        const key = panel.dataset.briefKey;
        if (key && !this.panelByKey.has(key)) {
          this.panelByKey.set(key, panel);
        }
      });

      if (!this.tabByKey.size) return;

      this.defaultKey = this.tabByKey.keys().next().value;
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.finder?.addEventListener('change', this.handleFinderChange);
      this.track?.addEventListener('scroll', this.updateCarouselControls, { passive: true });

      if ('ResizeObserver' in window && this.track) {
        this.resizeObserver = new ResizeObserver(this.updateCarouselControls);
        this.resizeObserver.observe(this.track);
        if (this.rail) this.resizeObserver.observe(this.rail);
      } else {
        window.addEventListener('resize', this.updateCarouselControls);
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      const initialState = this.resolveInitialState();
      if (initialState.hasExplicitTutorial && window.Shopify && window.Shopify.designMode) {
        this.initialRouteLockUntil = window.performance.now() + 1200;
      }
      this.activate(initialState.key, {
        animate: false,
        announce: false,
        updateUrl: false,
        scroll: false
      });

      if (initialState.cleanUrl) {
        this.replaceUrl(null);
      }

      this.carouselFrame = window.requestAnimationFrame(() => {
        this.updateCarouselControls();
        this.carouselFrame = null;
      });
      this.isInitialized = true;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.finder?.removeEventListener('change', this.handleFinderChange);
      this.track?.removeEventListener('scroll', this.updateCarouselControls);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('resize', this.updateCarouselControls);
      this.resizeObserver?.disconnect();

      if (this.animationTimer) window.clearTimeout(this.animationTimer);
      if (this.carouselFrame) window.cancelAnimationFrame(this.carouselFrame);

      this.animationTimer = null;
      this.carouselFrame = null;
      this.initialRouteLockUntil = 0;
      this.isInitialized = false;
    }

    resolveInitialState() {
      const url = new URL(window.location.href);
      const parameterNames = Array.from(url.searchParams.keys());

      if (!parameterNames.length) {
        return { key: this.defaultKey, cleanUrl: false, hasExplicitTutorial: false };
      }

      const tutorialValues = url.searchParams.getAll(this.paramName);
      const hasUnknownParameter = parameterNames.some(
        (name) => name !== this.paramName && !this.previewParameterNames.has(name)
      );
      const hasOneTutorialValue = tutorialValues.length === 1 && tutorialValues[0] !== '';
      const requestedKey = hasOneTutorialValue ? tutorialValues[0] : null;
      const isKnownTutorial = requestedKey ? this.tabByKey.has(requestedKey) : false;

      if (hasUnknownParameter || !hasOneTutorialValue || !isKnownTutorial) {
        const hasOnlyPreviewParameters = parameterNames.every((name) =>
          this.previewParameterNames.has(name)
        );

        return {
          key: this.defaultKey,
          cleanUrl: !hasOnlyPreviewParameters,
          hasExplicitTutorial: false
        };
      }

      return { key: requestedKey, cleanUrl: false, hasExplicitTutorial: true };
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) return;

      const scrollButton = event.target.closest('[data-brief-scroll]');
      if (scrollButton && this.contains(scrollButton)) {
        this.scrollCarousel(scrollButton.dataset.briefScroll);
        return;
      }

      const tab = event.target.closest('[data-brief-tab]');
      if (!tab || !this.contains(tab)) return;

      this.activate(tab.dataset.briefKey, {
        animate: true,
        announce: true,
        updateUrl: this.updateUrlOnChange,
        scroll: true
      });
    }

    handleKeydown(event) {
      if (!(event.target instanceof Element)) return;

      const currentTab = event.target.closest('[data-brief-tab]');
      if (!currentTab || !this.contains(currentTab)) return;

      const tabs = Array.from(this.tabByKey.values());
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      const isRtl = document.documentElement.dir === 'rtl';
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = isRtl
            ? (currentIndex - 1 + tabs.length) % tabs.length
            : (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = isRtl
            ? (currentIndex + 1) % tabs.length
            : (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      nextTab.focus({ preventScroll: true });
      this.activate(nextTab.dataset.briefKey, {
        animate: true,
        announce: true,
        updateUrl: this.updateUrlOnChange,
        scroll: true
      });
    }

    handleFinderChange(event) {
      const key = event.target.value;
      const tab = this.tabByKey.get(key);
      if (!tab) return;

      this.activate(key, {
        animate: true,
        announce: true,
        updateUrl: this.updateUrlOnChange,
        scroll: true
      });
      tab.focus({ preventScroll: true });
    }

    handleBlockSelect(event) {
      if (!event.detail || !event.detail.blockId) return;
      if (event.detail.load && window.performance.now() < this.initialRouteLockUntil) return;

      const tab = this.querySelector(
        '[data-brief-tab][data-block-id="' + event.detail.blockId + '"]'
      );
      if (!tab) return;

      this.activate(tab.dataset.briefKey, {
        animate: false,
        announce: false,
        updateUrl: false,
        scroll: true
      });
    }

    activate(key, options = {}) {
      const tab = this.tabByKey.get(key);
      const panel = this.panelByKey.get(key);
      if (!tab || !panel) return;

      const changed = this.activeKey !== key;
      this.activeKey = key;

      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((item) => {
        const isActive = item === panel;
        item.hidden = !isActive;
        item.classList.toggle('is-active', isActive);
        if (!isActive) item.classList.remove('is-entering');
      });

      this.mountPanel(panel);
      if (this.finder) this.finder.value = key;

      if (changed && options.animate) {
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');

        if (this.animationTimer) window.clearTimeout(this.animationTimer);
        this.animationTimer = window.setTimeout(() => {
          panel.classList.remove('is-entering');
          this.animationTimer = null;
        }, 280);
      }

      if (options.announce && this.status) {
        this.status.textContent = 'Showing filming brief for ' + (tab.dataset.briefTitle || '');
      }

      if (options.updateUrl) this.replaceUrl(key);
      if (options.scroll !== false) this.scrollTabIntoView(tab);
    }

    mountPanel(panel) {
      const template = panel.querySelector('[data-brief-template]');
      const mount = panel.querySelector('[data-brief-mount]');
      if (!template || !mount || mount.dataset.hydrated === 'true') return;

      mount.appendChild(template.content.cloneNode(true));
      mount.dataset.hydrated = 'true';
    }

    scrollCarousel(direction) {
      if (!this.track) return;

      const distance = Math.max(180, this.track.clientWidth * 0.72);
      const isRtl = document.documentElement.dir === 'rtl';
      const multiplier = direction === 'previous' ? -1 : 1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollBy({
        left: distance * multiplier * (isRtl ? -1 : 1),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    scrollTabIntoView(tab) {
      if (!this.track) return;

      const trackRect = this.track.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      let offset = 0;

      if (tabRect.left < trackRect.left) {
        offset = tabRect.left - trackRect.left;
      } else if (tabRect.right > trackRect.right) {
        offset = tabRect.right - trackRect.right;
      }

      if (Math.abs(offset) > 1) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: offset, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
    }

    updateCarouselControls() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      if (this.carouselFrame) window.cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = window.requestAnimationFrame(() => {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const hasOverflow = maxScroll > 2;
        this.previousButton.hidden = !hasOverflow;
        this.nextButton.hidden = !hasOverflow;
        this.previousButton.disabled = !hasOverflow || this.track.scrollLeft <= 1;
        this.nextButton.disabled = !hasOverflow || this.track.scrollLeft >= maxScroll - 1;
        this.carouselFrame = null;
      });
    }

    replaceUrl(key) {
      const url = new URL(window.location.href);
      const preservedPreviewParameters = [];

      if (window.Shopify && window.Shopify.designMode) {
        this.previewParameterNames.forEach((name) => {
          url.searchParams.getAll(name).forEach((value) => {
            preservedPreviewParameters.push([name, value]);
          });
        });
      }

      url.search = '';
      url.hash = '';
      if (key) url.searchParams.set(this.paramName, key);
      preservedPreviewParameters.forEach(([name, value]) => {
        url.searchParams.append(name, value);
      });

      try {
        window.history.replaceState(window.history.state, '', url.pathname + url.search);
      } catch (error) {
        console.warn('Creator video brief could not update the URL.', error);
      }
    }
  }

  customElements.define('creator-video-brief', CreatorVideoBrief);
})();
